import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listOrganizations, listReports, fmt, fmtInt } from "../lib/api";
import { Plus, ArrowRight, Building2, FileBarChart2, TrendingDown, Sparkles, Leaf } from "lucide-react";
import { Button } from "../components/ui/button";

export default function Dashboard() {
  const [orgs, setOrgs] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([listOrganizations(), listReports()])
      .then(([o, r]) => { setOrgs(o); setReports(r); })
      .finally(() => setLoading(false));
  }, []);

  const totalEmission = reports.reduce((s, r) => s + (r.results?.ozet?.total || 0), 0);
  const latestReport = reports[0];

  return (
    <div className="relative min-h-screen">
      {/* Hero */}
      <section className="relative overflow-hidden px-6 md:px-14 pt-12 md:pt-20 pb-12 grain">
        <div className="relative z-10 max-w-5xl">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-6">
            <Leaf className="size-3.5" />
            <span>Sera Gazı Envanteri · ISO 14064-1:2018</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-[64px] leading-[1.02] font-semibold text-foreground">
            Tesisinizin karbon<br />ayak izini<br />
            <span className="italic text-primary">hesaplayın.</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">
            Otel ve tesisler için DEFRA 2025 faktörleriyle 6 kategori sera gazı hesaplaması.
            Verilerinizi girin, Word raporu otomatik oluşsun.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/reports/new" data-testid="hero-new-report-btn">
              <Button size="lg" className="rounded-full h-12 px-7 gap-2 bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20">
                <Plus className="size-4" /> Yeni Rapor Oluştur
              </Button>
            </Link>
            <Link to="/organizations" data-testid="hero-orgs-btn">
              <Button size="lg" variant="outline" className="rounded-full h-12 px-7 gap-2 border-primary/20">
                Tesisleri Yönet <ArrowRight className="size-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="px-6 md:px-14 pb-14">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <StatCard icon={Building2} label="Kayıtlı Tesis" value={fmtInt(orgs.length)} />
          <StatCard icon={FileBarChart2} label="Toplam Rapor" value={fmtInt(reports.length)} />
          <StatCard icon={TrendingDown} label="Toplam Emisyon" value={`${fmt(totalEmission, 2)}`} suffix="ton CO₂e" />
        </div>
      </section>

      {/* Recent Reports + Orgs */}
      <section className="px-6 md:px-14 pb-20 grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3">
          <div className="flex items-end justify-between mb-5">
            <h2 className="font-display text-[28px] font-semibold">Son Raporlar</h2>
            <Link to="/reports" className="text-sm text-primary hover:underline">Tümünü gör →</Link>
          </div>
          {loading ? <div className="text-muted-foreground">Yükleniyor…</div> :
          reports.length === 0 ? (
            <EmptyState
              icon={FileBarChart2}
              title="Henüz rapor yok"
              description="İlk karbon ayak izi raporunuzu oluşturmak için başlayın."
              cta={<Link to="/reports/new"><Button className="rounded-full gap-2"><Plus className="size-4" /> Rapor Oluştur</Button></Link>}
            />
          ) : (
            <div className="space-y-3">
              {reports.slice(0, 5).map((r) => (
                <Link key={r.id} to={`/reports/${r.id}`} data-testid={`report-card-${r.id}`}
                      className="block p-5 rounded-2xl border border-border bg-card card-hover">
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <div className="font-display text-lg font-semibold truncate">{r.organization_name}</div>
                      <div className="text-sm text-muted-foreground">{r.year} yılı · {r.frequency || "12 aylık"}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Toplam</div>
                      <div className="font-display text-2xl font-semibold text-primary">
                        {fmt(r.results?.ozet?.total || 0, 2)}
                      </div>
                      <div className="text-[11px] text-muted-foreground">ton CO₂e</div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-2">
          <div className="flex items-end justify-between mb-5">
            <h2 className="font-display text-[28px] font-semibold">Tesisler</h2>
            <Link to="/organizations/new" className="text-sm text-primary hover:underline">+ Ekle</Link>
          </div>
          {loading ? <div className="text-muted-foreground">Yükleniyor…</div> :
          orgs.length === 0 ? (
            <EmptyState
              icon={Building2}
              title="Tesis yok"
              description="İlk otel / tesisinizi ekleyerek başlayın."
              cta={<Link to="/organizations/new"><Button className="rounded-full gap-2"><Plus className="size-4" /> Tesis Ekle</Button></Link>}
            />
          ) : (
            <div className="space-y-3">
              {orgs.slice(0, 5).map((o) => (
                <Link key={o.id} to={`/organizations/${o.id}/edit`} className="flex items-center gap-4 p-4 rounded-2xl border border-border bg-card card-hover">
                  <div className="size-12 rounded-xl bg-primary/10 flex items-center justify-center overflow-hidden">
                    {o.logo_base64 ? (
                      <img src={o.logo_base64} alt="" className="object-contain w-full h-full" />
                    ) : (
                      <Building2 className="size-5 text-primary" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{o.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{o.address || "—"}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-muted-foreground">Rapor</div>
                    <div className="font-semibold">{o.report_count}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, suffix }) {
  return (
    <div className="relative p-6 rounded-2xl border border-border bg-card card-hover animate-fadeInUp">
      <div className="flex items-start justify-between mb-6">
        <Icon className="size-6 text-primary" strokeWidth={1.8} />
        <Sparkles className="size-3.5 text-accent opacity-60" />
      </div>
      <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-1">{label}</div>
      <div className="font-display text-4xl font-semibold text-foreground leading-none">
        {value} {suffix && <span className="text-sm font-normal text-muted-foreground ml-1">{suffix}</span>}
      </div>
    </div>
  );
}

function EmptyState({ icon: Icon, title, description, cta }) {
  return (
    <div className="rounded-2xl border border-dashed border-border p-10 text-center">
      <Icon className="size-8 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
      <div className="font-display text-lg font-semibold mb-1">{title}</div>
      <div className="text-sm text-muted-foreground mb-5 max-w-sm mx-auto">{description}</div>
      {cta}
    </div>
  );
}
